import type { DeliverySlot } from "@utils/store/cart/useOrder";

type Slot = DeliverySlot["slots"][number];

/** 本地時間日期字串 YYYY-MM-DD */
export function todayStr(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

/** 當日已過時段：deliveryDate 為今天且 slot end_time 已過 → true（本地時間） */
export function isSlotPast(deliveryDate: string, endTime: string, now: Date = new Date()): boolean {
  if (deliveryDate !== todayStr(now)) return false;
  const [h, m] = endTime.split(":").map(Number);
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), h || 0, m || 0).getTime() <= now.getTime();
}

/** 容量判定：未帶 available_capacity 視為不限量；帶了則需 > 0 */
export function slotHasCapacity(s: Slot): boolean {
  return s.available_capacity !== undefined ? s.available_capacity > 0 : true;
}

/** 單一時段可預約：有容量且未過時 */
export function slotAvailable(date: string, s: Slot, now: Date = new Date()): boolean {
  return slotHasCapacity(s) && !isSlotPast(date, s.end_time, now);
}

/**
 * 可預約日期清單：API 有回、非整日公休、且至少一個可用時段。
 * 入口閘門與時段選擇器共用同一套判斷，確保「閘門說可預約」與「選擇器有可選日」一致。
 */
export function getReservableDays(slots: DeliverySlot[] | null | undefined, now: Date = new Date()): string[] {
  return (slots || [])
    .filter((d) => !d.is_closed_all_day && (d.slots || []).some((s) => slotAvailable(d.delivery_date, s, now)))
    .map((d) => d.delivery_date);
}

/** 是否還有任何可預約時段（公休但仍可預約其他日期時 → true） */
export function hasReservableSlots(slots: DeliverySlot[] | null | undefined, now: Date = new Date()): boolean {
  return getReservableDays(slots, now).length > 0;
}

/**
 * 目前選取的「日期＋時段」是否仍然成立：該日仍可預約、slot_id 仍在今次清單中、且未過時／未額滿。
 *
 * 只檢查 `!!slotId` 是不夠的：slotId 會從草稿（syncDraft）或 sessionStorage 殘留下來，
 * 而那個 id 可能已不在今次 deliverySlots 裡（換瓦斯行、換日、時段被移除）。
 * 這時畫面上沒有任何時段是選取狀態，「下一步」卻是可按的——使用者沒選時段就能進到下一步，
 * 還會帶著一個失效的 slot_id 去送單。
 */
export function isSlotSelected(
  slots: DeliverySlot[] | null | undefined,
  date: string | null,
  slotId: number | null,
  now: Date = new Date(),
): boolean {
  if (!date || slotId == null) return false;
  const day = (slots || []).find((d) => d.delivery_date === date);
  if (!day || day.is_closed_all_day) return false;
  const s = (day.slots || []).find((x) => x.slot_id === slotId);
  return !!s && slotAvailable(date, s, now);
}

/**
 * 「公休日且沒有任何可預約時段」時的入口行為（Kiosk 與 GasShop 兩個入口共用，切換只改這一行）：
 * - "block"：整頁擋成「尚未營業」（現行行為）
 * - "enter"：照樣進下單畫面；「立即配送」灰掉、點了跳休息中提示；預約配送顯示「目前無可預約日期」；不能送出
 */
export type ClosedNoSlotBehavior = "block" | "enter";
export const CLOSED_NO_SLOT_BEHAVIOR: ClosedNoSlotBehavior = "block";

/**
 * 入口閘門：能不能進下單畫面。
 * - 營業中 → 可
 * - 非營業但開放預約且有可預約時段 → 可（只能走預約）
 * - 非營業、開放預約但沒有任何可預約時段 → 依 CLOSED_NO_SLOT_BEHAVIOR
 * - 非營業又未開放預約 → 不可（尚未營業）
 */
export function canEnterOrder(
  {
    isOpenNow,
    allowReservation,
    slots,
  }: { isOpenNow: boolean; allowReservation: boolean; slots: DeliverySlot[] | null | undefined },
  behavior: ClosedNoSlotBehavior = CLOSED_NO_SLOT_BEHAVIOR,
  now: Date = new Date(),
): boolean {
  if (isOpenNow) return true;
  if (!allowReservation) return false;
  if (hasReservableSlots(slots, now)) return true;
  return behavior === "enter";
}

const TW_OFFSET_MS = 8 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
const WD_ONE = ["日", "一", "二", "三", "四", "五", "六"];
export const NEXT_OPEN_FALLBACK_MESSAGE = "現在休息中，請稍後再點選";

/**
 * 非營業時點「立即配送」的提示文字（以台灣時間 +08:00 判斷今天／明天）：
 * - 下一時段在今天：「現在休息中，請 HH:mm 再點選」
 * - 明天：「現在休息中，請明天 HH:mm 再點選」
 * - 後天以後：「現在休息中，請 M/D（週幾一字）HH:mm 再點選」
 * - 找不到（null／undefined／無法解析／已過去）：「現在休息中，請稍後再點選」
 */
export function formatNextOpen(nextOpenAt: string | null | undefined, now: Date = new Date()): string {
  if (!nextOpenAt) return NEXT_OPEN_FALLBACK_MESSAGE;
  const ms = new Date(nextOpenAt).getTime();
  if (!Number.isFinite(ms)) return NEXT_OPEN_FALLBACK_MESSAGE;
  // 早於（或等於）現在：store / sessionStorage 留下的舊值，不能拿來叫客戶等一個已過去的時間
  if (ms <= now.getTime()) return NEXT_OPEN_FALLBACK_MESSAGE;
  const tw = new Date(ms + TW_OFFSET_MS);
  const dayDiff = Math.floor((ms + TW_OFFSET_MS) / DAY_MS) - Math.floor((now.getTime() + TW_OFFSET_MS) / DAY_MS);
  const hhmm = `${String(tw.getUTCHours()).padStart(2, "0")}:${String(tw.getUTCMinutes()).padStart(2, "0")}`;
  if (dayDiff === 0) return `現在休息中，請 ${hhmm} 再點選`;
  if (dayDiff === 1) return `現在休息中，請明天 ${hhmm} 再點選`;
  return `現在休息中，請 ${tw.getUTCMonth() + 1}/${tw.getUTCDate()}（${WD_ONE[tw.getUTCDay()]}）${hhmm} 再點選`;
}
